import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import React from 'react';

import {useUpdateAgentSettings} from 'app/components/AgentExplorer/hooks';
import {BasemapView} from 'app/components/DeclarativeMap/ManageBasemapDialog';
import * as GlobalSettings from 'app/components/GlobalSettings/GlobalSettings';
import ImageFileUpload from 'app/components/GlobalSettings/ImageFileUpload';
import {NoteSortingMethod, noteSorters} from 'app/components/NotesReports/Sorts';
import {PushNotificationFn, usePushNotification} from 'app/components/Notification';
import {
  ApiOrganization,
  ApiOrganizationAreaUnit,
  ApiOrganizationUser,
  getAreaUnit,
  getOrgBasemapStyle,
  getOrgNoteDateFilter,
  getOrgNoteSortOrder,
  getShowSmartSummaries,
} from 'app/modules/Remote/Organization';
import {LoggedInUserActions} from 'app/providers/AuthProvider';
import {recordEvent} from 'app/tools/Analytics';
import * as CONSTANTS from 'app/utils/constants';
import featureFlags from 'app/utils/featureFlags';
import {useStateWithDeps} from 'app/utils/hookUtils';
import {MAPBOX_STYLES, MapStyle} from 'app/utils/mapUtils';
import {getOrgPrefix} from 'app/utils/organizationUtils';
import {isOwnerOrAdmin} from 'app/utils/userUtils';

import {OrganizationDefaultPropertyOverviewColumns} from './OrganizationDefaultPropertyOverviewColumns';
import {OrganizationDefaultReportText} from './OrganizationDefaultReportText';
import {OrganizationSettingDropdown} from './OrganizationSettingsDropdown';
import {OrganizationTags} from './OrganizationTagSettings';
import cs from './OrganizationView.module.scss';

const NAME_INPUT_ID = 'name-input';

const OrganizationView: React.FunctionComponent<
  React.PropsWithChildren<{
    organization: I.ImmutableOf<ApiOrganization>;
    profile: I.ImmutableOf<ApiOrganizationUser>;
    loggedInUserActions: LoggedInUserActions;
  }>
> = ({organization, profile, loggedInUserActions}) => {
  const pushNotification = usePushNotification();

  return (
    <GlobalSettings.Body title="Organization">
      <div className={cs.section}>
        <div className={cs.sectionHead}>
          <h1>Details</h1>
          <div className={cs.sectionHelpText}>
            These defaults affect all users in your organization.
          </div>
        </div>
        <div className={cs.sectionControls}>
          <div className={cs.stacked}>
            <OrganizationName
              organization={organization}
              profile={profile}
              pushNotification={pushNotification}
              setOrganizationName={loggedInUserActions.setOrganizationName}
            />
            <OrganizationUnits
              organization={organization}
              profile={profile}
              pushNotification={pushNotification}
              setOrganizationUnits={loggedInUserActions.setOrganizationUnits}
            />
            <OrganizationBasemap
              organization={organization}
              profile={profile}
              pushNotification={pushNotification}
              setOrganizationBasemap={loggedInUserActions.setOrganizationBasemap}
            />
            <OrganizationDefaultPropertyOverviewColumns
              organization={organization}
              profile={profile}
              pushNotification={pushNotification}
              setDefaultPropertyOverviewColumns={
                loggedInUserActions.setOrganizationPropertyOverviewColumnSettings
              }
            />
          </div>
        </div>
      </div>
      <div className={cs.section}>
        <div className={cs.sectionHead}>
          <h1>Lens Agent</h1>
          <div className={cs.sectionHelpText}>
            Identify structures, detect changes, and describe properties.
          </div>
        </div>
        <div className={cs.sectionControls}>
          <div className={cs.stacked}>
            <OrganizationAgentVisibility
              organization={organization}
              profile={profile}
              pushNotification={pushNotification}
            />
          </div>
        </div>
      </div>
      <div className={cs.section}>
        <div className={cs.sectionHead}>
          <h1>Reports</h1>
          <div className={cs.sectionHelpText}>
            Reports are collections of notes. You can add additional context or observations.
          </div>
        </div>
        <div className={cs.sectionControls}>
          <div className={cs.stacked}>
            {featureFlags.ORGANIZATION_LOGO_UPLOAD(organization, profile) && (
              <div className={cs.inline}>
                <OrganizationLogo
                  organization={organization}
                  pushNotification={pushNotification}
                  setOrganizationLogo={loggedInUserActions.setOrganizationLogo}
                />
              </div>
            )}
            <OrganizationDefaultReportText
              organization={organization}
              profile={profile}
              pushNotification={pushNotification}
              setDefaultReportText={loggedInUserActions.setOrganizationDefaultReportText}
            />
            <OrganizationShowSmartSummaries
              organization={organization}
              profile={profile}
              pushNotification={pushNotification}
              setOrganizationShowSmartSummaries={
                loggedInUserActions.setOrganizationShowSmartSummaries
              }
            />
          </div>
        </div>
      </div>
      <div className={cs.section}>
        <div className={cs.sectionHead}>
          <h1>Tags</h1>
          <div className={cs.sectionHelpText}>
            Tags help you organize, label, and filter properties and notes.
          </div>
        </div>
        <div className={cs.sectionControls}>
          <div className={cs.stacked}>
            <OrganizationTags
              organization={organization}
              profile={profile}
              setTags={loggedInUserActions.setOrganizationTags}
            />
          </div>
        </div>
      </div>

      <div className={cs.section}>
        <div className={cs.sectionHead}>
          <h1>Notes</h1>
          <div className={cs.sectionHelpText}>
            Notes are for saving observations about properties and imagery.
          </div>
        </div>
        <div className={cs.sectionControls}>
          <div className={cs.stacked}>
            <OrganizationNoteSortOrder
              organization={organization}
              profile={profile}
              pushNotification={pushNotification}
              setOrganizationNoteSortOrder={loggedInUserActions.setOrganizationNoteSortOrder}
            />
            <OrganizationNoteDateFilter
              organization={organization}
              profile={profile}
              pushNotification={pushNotification}
              setOrganizationNoteDateFilter={loggedInUserActions.setOrganizationNoteDateFilter}
            />
          </div>
        </div>
      </div>
    </GlobalSettings.Body>
  );
};

const OrganizationBasemap: React.FunctionComponent<
  React.PropsWithChildren<{
    organization: I.ImmutableOf<ApiOrganization>;
    profile: I.ImmutableOf<ApiOrganizationUser>;
    pushNotification: PushNotificationFn;
    setOrganizationBasemap: LoggedInUserActions['setOrganizationBasemap'];
  }>
> = ({organization, profile, pushNotification, setOrganizationBasemap}) => {
  const id = organization.get('id');
  const basemapStyle: MapStyle = getOrgBasemapStyle(organization);
  const canUpdate = isOwnerOrAdmin(profile.get('role'));

  const [isUpdating, setIsUpdating] = React.useState(false);

  const changeOrgBasemap = React.useCallback(
    async (value: MapStyle) => {
      try {
        setIsUpdating(true);
        await setOrganizationBasemap(id, value);
        pushNotification({
          message: 'Basemap style updated.',
          autoHideDuration: 3000,
          options: {
            intent: B.Intent.SUCCESS,
          },
        });
      } catch (_e) {
        pushNotification({
          message: `There was a problem updating basemap style. Please try again.`,
          autoHideDuration: 3000,
          options: {
            intent: B.Intent.DANGER,
          },
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [id, pushNotification, setOrganizationBasemap]
  );

  return (
    <div className={cs.field}>
      <label>Basemap style</label>
      <div>
        <B.Popover
          content={
            <div style={{padding: '1rem 1.5rem'}} className={B.Classes.POPOVER_DISMISS}>
              <BasemapView
                selectedStyle={basemapStyle}
                onChangeMapStyle={(value) => changeOrgBasemap(value)}
              />
            </div>
          }
          position={'right'}
        >
          <B.Tooltip
            content={'Only organization admins can update this setting.'}
            disabled={canUpdate}
          >
            <B.AnchorButton
              text={MAPBOX_STYLES.find((s) => s.id === basemapStyle)!.title}
              rightIcon="double-caret-vertical"
              className={cs.selectControl}
              disabled={isUpdating || !canUpdate}
            />
          </B.Tooltip>
        </B.Popover>
      </div>
    </div>
  );
};

const OrganizationUnits: React.FunctionComponent<
  React.PropsWithChildren<{
    organization: I.ImmutableOf<ApiOrganization>;
    profile: I.ImmutableOf<ApiOrganizationUser>;
    pushNotification: PushNotificationFn;
    setOrganizationUnits: LoggedInUserActions['setOrganizationUnits'];
  }>
> = ({organization, profile, pushNotification, setOrganizationUnits}) => {
  const unitTypes: Record<string, string> = {
    [CONSTANTS.UNIT_AREA_ACRE]: 'Acres and feet', // Imperial
    [CONSTANTS.UNIT_AREA_HECTARE]: 'Hectares and meters', // Metric
  };
  const units: ApiOrganizationAreaUnit = getAreaUnit(organization);

  return (
    <OrganizationSettingDropdown
      label="Units"
      value={units}
      options={unitTypes}
      profile={profile}
      onChange={setOrganizationUnits}
      organization={organization}
      pushNotification={pushNotification}
    />
  );
};

const OrganizationNoteSortOrder: React.FunctionComponent<
  React.PropsWithChildren<{
    organization: I.ImmutableOf<ApiOrganization>;
    profile: I.ImmutableOf<ApiOrganizationUser>;
    pushNotification: PushNotificationFn;
    setOrganizationNoteSortOrder: LoggedInUserActions['setOrganizationNoteSortOrder'];
  }>
> = ({organization, profile, pushNotification, setOrganizationNoteSortOrder}) => {
  const noteSortOrder = getOrgNoteSortOrder(organization);

  // make dropdown options out of our noteSorters object
  const noteSortOptions = Object.entries(noteSorters).reduce(
    (acc, [noteSortDictKey, noteSortDictValue]) => {
      acc[noteSortDictKey] = noteSortDictValue.displayName;
      return acc;
    },
    {} as Record<NoteSortingMethod, string>
  );

  return (
    <OrganizationSettingDropdown
      label="Note sort order"
      value={noteSortOrder}
      options={noteSortOptions}
      profile={profile}
      onChange={setOrganizationNoteSortOrder}
      organization={organization}
      pushNotification={pushNotification}
    />
  );
};

const OrganizationNoteDateFilter: React.FunctionComponent<
  React.PropsWithChildren<{
    organization: I.ImmutableOf<ApiOrganization>;
    profile: I.ImmutableOf<ApiOrganizationUser>;
    pushNotification: PushNotificationFn;
    setOrganizationNoteDateFilter: LoggedInUserActions['setOrganizationNoteDateFilter'];
  }>
> = ({organization, profile, pushNotification, setOrganizationNoteDateFilter}) => {
  const noteDateFilter = getOrgNoteDateFilter(organization);

  const noteDateFilterOptions: Record<string, string> = {
    last_year: 'Past 12 months',
    year_to_date: 'Year to date',
    all_notes: 'All notes',
  };

  return (
    <OrganizationSettingDropdown
      label="Note date filter"
      value={noteDateFilter}
      options={noteDateFilterOptions}
      profile={profile}
      onChange={(orgId, value) => setOrganizationNoteDateFilter(orgId, value)}
      organization={organization}
      pushNotification={pushNotification}
    />
  );
};

const OrganizationShowSmartSummaries: React.FunctionComponent<
  React.PropsWithChildren<{
    organization: I.ImmutableOf<ApiOrganization>;
    profile: I.ImmutableOf<ApiOrganizationUser>;
    pushNotification: PushNotificationFn;
    setOrganizationShowSmartSummaries: LoggedInUserActions['setOrganizationShowSmartSummaries'];
  }>
> = ({organization, profile, pushNotification, setOrganizationShowSmartSummaries}) => {
  const shouldShowSmartSummaries: boolean = getShowSmartSummaries(organization);

  // smart summaries are stored as a boolean in the org settings, but we're using string
  // to display and update the values, hence the identical string key/values for the options
  const options: Record<string, string> = {
    Available: 'Available',
    Hidden: 'Hidden',
  };

  return (
    <OrganizationSettingDropdown
      label="Smart summaries in reports"
      value={shouldShowSmartSummaries ? options.Available : options.Hidden}
      options={options}
      profile={profile}
      onChange={(orgId, value) =>
        setOrganizationShowSmartSummaries(orgId, value === options.Available)
      }
      organization={organization}
      pushNotification={pushNotification}
      supportLink="https://lens.tech/support/article/158-smart-summary"
    />
  );
};

/**
 * Lets an organization owner toggle whether the Lens Agent is hidden for their
 * team. Unlike the other org settings, agent settings live behind a dedicated
 * owner-only endpoint (see useUpdateAgentSettings), so this control is gated on
 * strict owner rather than the owner-or-admin check used elsewhere.
 */
const OrganizationAgentVisibility: React.FunctionComponent<
  React.PropsWithChildren<{
    organization: I.ImmutableOf<ApiOrganization>;
    profile: I.ImmutableOf<ApiOrganizationUser>;
    pushNotification: PushNotificationFn;
  }>
> = ({organization, profile, pushNotification}) => {
  const agentSettings = React.useMemo(
    () => organization.getIn(['settings', CONSTANTS.ORG_SETTING_AGENT_SETTINGS])?.toJS(),
    [organization]
  ) as {run_quota: number; enabled: boolean; hidden: boolean} | undefined;

  // An org that has never turned the Agent on has no agent_settings object at
  // all; in that case we surface an "enable" button rather than the toggle.
  const hasAgentSettings =
    !agentSettings || (!!agentSettings && Object.keys(agentSettings).length > 0);
  const isHidden = !!agentSettings?.hidden;
  const isOwner = profile.get('role') === CONSTANTS.USER_ROLE_OWNER;

  const updateAgentSettings = useUpdateAgentSettings(organization.get('id'));

  const options: Record<string, string> = {
    Available: 'Available',
    Hidden: 'Hidden',
  };

  const handleOnChange = async (value: string) => {
    try {
      await updateAgentSettings.mutateAsync({hidden: value === options.Hidden});
      pushNotification({
        message: 'Agent visibility updated.',
        autoHideDuration: 3000,
        options: {
          intent: B.Intent.SUCCESS,
        },
      });
    } catch (_e) {
      // Handled by useUpdateAgentSettings.onError.
    }
  };

  const handleEnable = async () => {
    try {
      await updateAgentSettings.mutateAsync({enabled: true});
      pushNotification({
        message: `Agent enabled for ${organization.get('name')}.`,
        autoHideDuration: 3000,
        options: {
          intent: B.Intent.SUCCESS,
        },
      });
    } catch (_e) {
      // Handled by useUpdateAgentSettings.onError.
    }
  };

  return (
    <div>
      <B.Tooltip
        content={'Only organization owners can update this setting.'}
        disabled={isOwner}
        className={cs.selectControl}
      >
        {hasAgentSettings ? (
          <B.HTMLSelect
            disabled={updateAgentSettings.isPending || !isOwner}
            fill
            value={isHidden ? options.Hidden : options.Available}
            onChange={(e) => handleOnChange(e.currentTarget.value)}
            aria-label={`Change Lens Agent visibility for ${organization.get('name')}`}
          >
            {Object.entries(options).map(([optionValue, displayName]) => (
              <option key={optionValue} value={optionValue}>
                {displayName}
              </option>
            ))}
          </B.HTMLSelect>
        ) : (
          <B.AnchorButton
            className={cs.agentButton}
            text="Start Agent trial"
            intent="primary"
            disabled={!isOwner}
            loading={updateAgentSettings.isPending}
            onClick={handleEnable}
          />
        )}
      </B.Tooltip>
      <div className={cs.agentFieldHelpText}>
        {!isOwner ? (
          <>
            Reach out to your Lens account owner to{' '}
            {hasAgentSettings ? 'update this setting' : 'enable'} for your organization. Find them{' '}
            <a
              href={`/${getOrgPrefix(organization)}/settings/team`}
              rel="noopener noreferrer"
              target="_blank"
            >
              here
            </a>
            .
          </>
        ) : hasAgentSettings ? (
          <>
            Show or hide the Lens Agent for all your users. This can be adjusted at any time by
            account owners.
          </>
        ) : (
          <>
            <div style={{marginBottom: '0.5rem'}}>
              Start a free trial for your organization. Your team can use preset prompts to explore
              what Agent can do, and you can turn it off at any time.
            </div>

            <div>
              We've made a few updates to our terms to reference this new capability. You agree that
              clicking the button above and turning on the Agent constitutes your written
              acceptance, on behalf of your organization, to the updated{' '}
              <a href="https://lens.tech/terms/" target="_blank" rel="noopener noreferrer">
                Terms of Service
              </a>
              .
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const OrganizationLogo: React.FunctionComponent<
  React.PropsWithChildren<{
    organization: I.ImmutableOf<ApiOrganization>;
    pushNotification: PushNotificationFn;
    setOrganizationLogo: LoggedInUserActions['setOrganizationLogo'];
  }>
> = ({organization, pushNotification, setOrganizationLogo}) => {
  const id = organization.get('id');
  const logo = organization.getIn(['settings', 'logo']);

  const [isUpdating, setIsUpdating] = React.useState(false);

  const removeFile = React.useCallback(async () => {
    try {
      setIsUpdating(true);
      await setOrganizationLogo(id);
      recordEvent('Unset organization logo');
    } catch (_e) {
      pushNotification({
        message: 'There was a problem removing your logo. Please try again.',
        autoHideDuration: 3000,
        options: {
          intent: B.Intent.DANGER,
        },
      });
    } finally {
      setIsUpdating(false);
    }
  }, [id, pushNotification, setOrganizationLogo]);

  const uploadFile = React.useCallback(
    async (file: File) => {
      try {
        setIsUpdating(true);
        await setOrganizationLogo(id, file);
        recordEvent('Set organization logo');
      } catch (_e) {
        pushNotification({
          message: 'There was a problem uploading your logo. Please try again.',
          autoHideDuration: 3000,
          options: {
            intent: B.Intent.DANGER,
          },
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [id, pushNotification, setOrganizationLogo]
  );

  return (
    <div className={cs.field}>
      <label>Logo</label>
      <div className={cs.logo}>
        <p>
          Your logo will appear on the title page of{' '}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://lens.tech/support/article/18-generating-reports"
          >
            reports
          </a>
          .
        </p>
        <ImageFileUpload
          label="Logo"
          removeFile={removeFile}
          uploadFile={uploadFile}
          isUpdating={isUpdating}
          url={logo ? new URL(logo) : undefined}
        />
      </div>
    </div>
  );
};

const OrganizationName: React.FunctionComponent<
  React.PropsWithChildren<{
    organization: I.ImmutableOf<ApiOrganization>;
    profile: I.ImmutableOf<ApiOrganizationUser>;
    pushNotification: PushNotificationFn;
    setOrganizationName: LoggedInUserActions['setOrganizationName'];
  }>
> = ({organization, profile, pushNotification, setOrganizationName}) => {
  const id = organization.get('id');
  const name = organization.get('name');

  const isEditor = isOwnerOrAdmin(profile.get('role'));

  /**
   * Using useStateWithDeps to update the field if organization changes after render.
   *
   * At the time of writing the case for this is related to how the AuthProvider
   * determines what the current organization is. When a parent profile user looks
   * at a child org, this component will briefly render with the parent org's
   * settings. Asynchronously a useEffect in WithLoggedInAuth will see that we're
   * looking at a child org, and reset the current organization to the child org.
   * */
  const [nameFieldValue, setNameFieldValue] = useStateWithDeps(name, [name]);
  const [isUpdating, setIsUpdating] = React.useState(false);

  const saveName = React.useCallback(async () => {
    // No-op if we don't yet have an input element reference or if the current
    // input value has not changed.
    if (nameFieldValue === name) {
      return;
    }

    // Reset the input and no-op if the user attempts to save a name consisting
    // exclusively of whitespace.
    if (!nameFieldValue.trim()) {
      setNameFieldValue(name);
      return;
    }

    try {
      setIsUpdating(true);
      await setOrganizationName(id, nameFieldValue);
      pushNotification({
        message: 'Organization name updated.',
        autoHideDuration: 3000,
        options: {
          intent: B.Intent.SUCCESS,
        },
      });
    } catch (_e) {
      // Reset the input and display an error toast if the update fails.
      setNameFieldValue(name);
      pushNotification({
        message: 'There was a problem updating the organization name. Please try again.',
        autoHideDuration: 3000,
        options: {
          intent: B.Intent.DANGER,
        },
      });
    } finally {
      setIsUpdating(false);
    }
  }, [id, name, nameFieldValue, pushNotification, setNameFieldValue, setOrganizationName]);

  return (
    <div className={cs.field}>
      <label htmlFor={NAME_INPUT_ID}>Name</label>
      {isEditor ? (
        <B.InputGroup
          id={NAME_INPUT_ID}
          disabled={isUpdating}
          value={nameFieldValue}
          onValueChange={(value) => setNameFieldValue(value)}
          onBlur={saveName}
        />
      ) : (
        name
      )}
    </div>
  );
};

export default OrganizationView;
